import { LottieLoading } from "../../../components/Loading/LottieLoading";
import { ReactComponent as More } from "../../../assets/icons/more.svg";
import dayjs from "dayjs";
import { ActionView } from "./ActionView";
import UIPopover from "../../../components/UIPopover";
import { GroupTag } from "../sales-aid-modal/components/GroupTag";
import { useApp } from "../../../services/app-context";
import { api } from "../../../services/api";
import { ShowToast } from "../../../services/toast";
import { useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { ReactComponent as SortIcon } from "../../../assets/icons/sort.svg";
import { ReactComponent as ChevronDown } from "../../../assets/icons/chevron-down.svg";
import { nextSort, SortKey, SortState } from "./tableSort";

export function GroupTagList({ groups, groupOptions }: { groups: any[] | null | undefined; groupOptions: any[] }) {
    const safeGroups = groups ?? [];
    const selectedGroups = groupOptions.filter((group) => safeGroups.includes(group.key));
    return (
        <div className="flex items-center gap-2 flex-wrap">
            {selectedGroups.map(group => <GroupTag key={group.key} selectedGroup={group} />)}
            {selectedGroups.length === 0 && "-"}
        </div>
    )
}

function SortableHeader({ label, sortKey, sort, onSort, className }: { label: string, sortKey: SortKey, sort: SortState, onSort: (sort: SortState) => void, className?: string }) {
    const isActive = sort.key === sortKey;
    return (
        <th
            className={className}
            aria-sort={isActive ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
        >
            <button
                type="button"
                data-testid={`sales-aid-sort-${sortKey}`}
                onClick={() => onSort(nextSort(sort, sortKey))}
                className={twMerge("flex items-center gap-1.5 uppercase whitespace-nowrap hover:text-black-600", isActive && "text-black-600")}
            >
                {label}
                {isActive ? (
                    <ChevronDown className={twMerge("w-2.5 h-2.5", sort.direction === "asc" && "rotate-180")} />
                ) : (
                    <SortIcon className="w-3.5 h-3.5 opacity-50" />
                )}
            </button>
        </th>
    );
}

export default function MaterialTable({ data, isLoading, isSorting = false, error, onRetry, onDelete, isAdmin, onEdit, onPreview, showCohort, sort, onSort }: { data: any[], isLoading: boolean, isSorting?: boolean, error: any, onRetry: () => void, onDelete: (file: any) => void, isAdmin: boolean, onEdit: (file: any) => void, onPreview: (file: any) => void, showCohort: boolean, sort: SortState, onSort: (sort: SortState) => void }) {
    const { tenantProfile } = useApp();
    const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    // A new sort starts from its first rows, which also keeps the loading overlay in view.
    const handleSort = (next: SortState) => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
        onSort(next);
    };

    const handleDownload = async (file: any) => {
        const idKey = String(file.id);
        try {
            setDownloadingFileId(idKey);
            const response = await api.getSalesAidFileSignedURL(file.id);
            if (response.status !== 200 || !response.data?.signedUrl) {
                ShowToast({ message: "Failed to download file", type: "error" });
                return;
            }

            const signedUrl = response.data.signedUrl as string;
            const fileRes = await fetch(signedUrl);
            if (!fileRes.ok) throw new Error("Failed to fetch file");

            const blob = await fileRes.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = file.file_name || "download";
            a.rel = "noopener noreferrer";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(blobUrl);
        } catch {
            ShowToast({ message: "Failed to download file", type: "error" });
        } finally {
            setDownloadingFileId((current) => (current === idKey ? null : current));
        }
    };

    return (
        <div ref={scrollRef} className="relative max-h-[calc(100vh-340px)] overflow-y-auto w-full" data-testid="sales-aid-table-scroll">
            <table className="w-full">
                <colgroup>
                    <col className="w-[35%]" />
                    <col className="w-[20%]" />
                    {showCohort && <col className="w-[25%]" />}
                    <col className="w-[10%]" />
                    <col className="w-[5%]" />
                </colgroup>
                <thead className="sticky top-0 z-[1] !border-b-[0px]">
                    <tr className="[&>th]:text-xs [&>th]:bg-background2 [&>th]:text-left [&>th]:font-semibold  [&>th]:text-gray-701 [&>th]:!h-9">
                        <SortableHeader label="FILE NAME" sortKey="file_name" sort={sort} onSort={handleSort} className="pl-4 !rounded-tl-[9px]" />
                        <SortableHeader label="DOC CODE" sortKey="code" sort={sort} onSort={handleSort} />
                        {showCohort && <SortableHeader label="COHORT" sortKey="cohort" sort={sort} onSort={handleSort} />}
                        <SortableHeader label="DATE UPLOADED" sortKey="date_uploaded" sort={sort} onSort={handleSort} />
                        <th className="rounded-tr-[9px]"></th>
                    </tr>
                </thead>
                <tbody className={twMerge("[&_td]:py-2", isSorting && "invisible")} data-testid="sales-aid-rows">
                    {!isLoading && data.map((item) => (
                        <tr className="text-[17px]" key={item.id} data-testid="sales-aid-row">
                            <td className="pl-4 text-black-600 text-xs">{item.file_name}</td>
                            <td className="text-black-600 text-xs">{item.code || "-"}</td>
                            {showCohort && <td>
                                <div>
                                    <GroupTagList groups={item.groups} groupOptions={tenantProfile?.groups ?? []} />
                                </div>
                            </td>}
                            <td className="text-gray-601 text-xs">{dayjs(item.created_at).format("MMM DD, YYYY")}</td>
                            <td>
                                <UIPopover
                                    trigger={
                                        <span className="block p-2">
                                            <More />
                                        </span>
                                    }
                                >
                                    {(close) => (
                                        <ActionView
                                            onDelete={() => {
                                                onDelete(item);
                                                close();
                                            }}
                                            onEdit={() => {
                                                onEdit(item);
                                                close();
                                            }}
                                            isAdmin={isAdmin}
                                            onPreview={() => {
                                                onPreview(item);
                                                close();
                                            }}
                                            onDownload={() => {
                                                handleDownload(item);
                                            }}
                                            isDownloading={downloadingFileId === String(item.id)}
                                        />
                                    )}
                                </UIPopover>
                            </td>
                        </tr>
                    ))}


                    {isLoading && (
                        <tr>
                            <td colSpan={showCohort ? 5 : 4} className="text-center">
                                <LottieLoading className="h-[calc(100vh-500px)]" />
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {isSorting && (
                <div className="absolute inset-x-0 top-9 bottom-0 flex items-center justify-center" data-testid="sales-aid-sorting">
                    <LottieLoading className="h-full" />
                </div>
            )}
            {!isLoading && data.length === 0 && (
                <div className="text-center w-full h-20 flex items-center justify-center gap-1" data-testid="sales-aid-empty">
                    {error ? (
                        <>
                            Couldn't load documents.
                            <button type="button" onClick={onRetry} data-testid="sales-aid-retry" className="font-semibold underline hover:opacity-70">
                                Try again
                            </button>
                        </>
                    ) : "No data found"}
                </div>
            )}
            {/* {
                !isLoading && data.length === 0 && (
                    <div className="text-center w-full h-[calc(100vh-500px)] flex items-center justify-center">No data found</div>
                )
            } */}
            {/* <Button variant="primary" onClick={onAddPracticeMaterials} className="mt-4">Add Practice Materials</Button> */}
        </div>
    )
}